import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { RefrescoSesionService } from './refresco-sesion.service';

/**
 * Ante un 401, intenta renovar la sesion y reintenta la peticion UNA vez. Si la
 * renovacion tambien falla, cierra sesion y vuelve al formulario.
 *
 * El refresco NO se llama a intervalo: lo dispara el 401. La peticion que se
 * reintenta ya paso por `credentialsInterceptor`, asi que conserva
 * `withCredentials` y el navegador adjunta la cookie nueva.
 */

/**
 * Endpoints de sesion, EXENTOS.
 *
 * ⚠️ Sin esto pasan dos cosas, y ninguna da un error que se entienda:
 *
 *   · El 401 de `refrescar` dispararia otro refresco, y ese otro, y otro: un
 *     bucle que solo para cuando el navegador se rinde.
 *   · El 401 legitimo de `autenticar` —contraseña incorrecta— acabaria
 *     expulsando al usuario del propio login, y el error que ve **no es el
 *     suyo**: en vez de "contraseña incorrecta" lee "tu sesion expiro".
 */
const EXENTOS = ['/sesion/autenticar', '/sesion/refrescar', '/sesion/cerrar'];

export const sesionExpiradaInterceptor: HttpInterceptorFn = (req, next) => {
  // Con la autenticacion simulada no hay backend de sesion al que preguntar: los
  // 401 que aparezcan son de otros servicios y deben llegar al componente.
  if (environment.authSimulado) return next(req);

  const refresco = inject(RefrescoSesionService);
  const auth = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      const es401 = error instanceof HttpErrorResponse && error.status === 401;
      if (!es401 || EXENTOS.some((ruta) => req.url.includes(ruta))) {
        return throwError(() => error);
      }

      return refresco.refrescar().pipe(
        switchMap(() => next(req)),
        catchError(() =>
          auth.cerrarSesion().pipe(
            switchMap(() => {
              void router.navigate(['/login']);
              return throwError(() => error);
            }),
          ),
        ),
      );
    }),
  );
};
